import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  Calendar, MapPin, Clock, Image, DollarSign, MessageCircle,
  Link as LinkIcon, Users, ArrowLeft, Upload, X, Loader2, Info,
  Ticket, Plus, Trash2, Video, Play, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth, useIsland, API } from '../App';
import { MobileSelect } from '../components/ui/mobile-select';

const MAX_IMAGES = 10;
const MAX_VIDEOS = 2;

const CreateEventPage = () => {
  const navigate = useNavigate();
  const { eventId: eventIdOrSlug } = useParams(); // Get eventId or slug from URL for edit mode
  const isEditMode = Boolean(eventIdOrSlug);
  
  const { user, token } = useAuth();
  const { selectedIsland } = useIsland();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [hasTickets, setHasTickets] = useState(false);
  const [showTicketStats, setShowTicketStats] = useState(true);
  const [tickets, setTickets] = useState([
    { name: 'General Admission', price: 0, quantity: 100, description: '' }
  ]);
  
  // Store the actual event UUID (different from slug in URL)
  const [actualEventId, setActualEventId] = useState(null);
  
  // Multi-image and video state
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    event_date: '',
    event_time: '',
    end_date: '',
    end_time: '',
    venue_name: '',
    address: '',
    city: '',
    island: selectedIsland !== null ? selectedIsland : 'curacao',
    latitude: '',
    longitude: '',
    price: 0,
    price_currency: 'USD',
    is_free: false,
    organizer_whatsapp: '',
    external_link: '',
    info_url: '',
    facebook_url: '',
    instagram_url: '',
    capacity: ''
  });

  const addTicketType = () => {
    setTickets([...tickets, { name: '', price: 0, quantity: 50, description: '' }]);
  };

  const removeTicketType = (index) => {
    if (tickets.length > 1) {
      setTickets(tickets.filter((_, i) => i !== index));
    }
  };

  const updateTicket = (index, field, value) => {
    const newTickets = [...tickets];
    newTickets[index] = { ...newTickets[index], [field]: value };
    setTickets(newTickets);
  };

  useEffect(() => {
    // Wait for user context to be initialized before checking auth
    // The user might be null initially while loading from localStorage
    const timeout = setTimeout(() => {
      if (!user) {
        toast.error('Please sign in to create an event');
        navigate('/auth');
        return;
      }
    }, 500);
    
    fetchCategories();
    
    // If edit mode, fetch existing event data
    if (isEditMode && eventIdOrSlug) {
      fetchEventData();
    }
    
    return () => clearTimeout(timeout);
  }, [user, eventIdOrSlug, isEditMode]);

  const fetchEventData = async () => {
    setInitialLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventIdOrSlug}`);
      const event = res.data;
      
      // Store the actual UUID for later use in PUT request
      setActualEventId(event.id);
      
      // Check if user is the owner
      if (event.user_id !== user?.id) {
        toast.error('You are not authorized to edit this event');
        navigate('/events');
        return;
      }
      
      // Parse dates
      const eventDate = new Date(event.event_date);
      const endDate = event.end_date ? new Date(event.end_date) : null;
      
      // Set form data
      setFormData({
        title: event.title || '',
        description: event.description || '',
        category: event.category || '',
        event_date: eventDate.toISOString().split('T')[0],
        event_time: eventDate.toTimeString().slice(0, 5),
        end_date: endDate ? endDate.toISOString().split('T')[0] : '',
        end_time: endDate ? endDate.toTimeString().slice(0, 5) : '',
        venue_name: event.location?.venue_name || '',
        address: event.location?.address || '',
        city: event.location?.city || '',
        island: event.location?.island || 'curacao',
        latitude: event.location?.latitude || '',
        longitude: event.location?.longitude || '',
        price: event.price || 0,
        price_currency: event.price_currency || 'USD',
        is_free: event.is_free || false,
        organizer_whatsapp: event.organizer_whatsapp || '',
        external_link: event.external_link || '',
        info_url: event.info_url || '',
        facebook_url: event.facebook_url || '',
        instagram_url: event.instagram_url || '',
        capacity: event.capacity || ''
      });
      
      // Set images and videos
      if (event.images && event.images.length > 0) {
        setImages(event.images);
      } else if (event.image) {
        setImages([event.image]);
      }
      
      if (event.videos && event.videos.length > 0) {
        setVideos(event.videos);
      }
      
      // Set tickets
      if (event.tickets && event.tickets.length > 0) {
        setHasTickets(true);
        setTickets(event.tickets);
        setShowTicketStats(event.show_ticket_stats !== false);
      }
      
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Failed to load event data');
      navigate('/events');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/events/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Multi-image upload handler
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.warning(`Only uploading ${remainingSlots} image(s). Max ${MAX_IMAGES} allowed.`);
    }

    setImageUploading(true);
    
    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const res = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });
        setImages(prev => [...prev, res.data.url]);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setImageUploading(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Video upload handler
  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = MAX_VIDEOS - videos.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_VIDEOS} videos allowed`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setVideoUploading(true);

    for (const file of filesToUpload) {
      if (!file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a video`);
        continue;
      }

      // Check file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 100MB limit`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        // Use the video-specific upload endpoint
        const res = await axios.post(`${API}/upload/video`, formDataUpload, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });
        setVideos(prev => [...prev, res.data.url]);
        toast.success('Video uploaded successfully');
      } catch (error) {
        const errorMsg = error.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(errorMsg);
      }
    }

    setVideoUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check each required field and show specific error
    const missingFields = [];
    if (!formData.title) missingFields.push('Title');
    if (!formData.description) missingFields.push('Description');
    if (!formData.category) missingFields.push('Category');
    if (!formData.event_date) missingFields.push('Start Date');
    if (!formData.address) missingFields.push('Address');
    if (!formData.island) missingFields.push('Island');

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      // Combine date and time
      const eventDateTime = formData.event_time 
        ? `${formData.event_date}T${formData.event_time}:00` 
        : `${formData.event_date}T00:00:00`;
      
      let endDateTime = null;
      if (formData.end_date) {
        endDateTime = formData.end_time 
          ? `${formData.end_date}T${formData.end_time}:00` 
          : `${formData.end_date}T23:59:59`;
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        event_date: new Date(eventDateTime).toISOString(),
        end_date: endDateTime ? new Date(endDateTime).toISOString() : null,
        location: {
          venue_name: formData.venue_name || null,
          address: formData.address,
          city: formData.city || null,
          island: formData.island,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null
        },
        image: images[0] || null,  // Legacy field - first image
        images: images,  // All images (max 10)
        videos: videos,  // All videos (max 2)
        price: hasTickets ? 0 : (parseFloat(formData.price) || 0),
        price_currency: formData.price_currency,
        is_free: formData.is_free,
        organizer_whatsapp: formData.organizer_whatsapp || null,
        external_link: formData.is_free ? null : (formData.external_link || null),
        info_url: formData.info_url || null,
        facebook_url: formData.facebook_url || null,
        instagram_url: formData.instagram_url || null,
        capacity: hasTickets 
          ? tickets.reduce((sum, t) => sum + (t.quantity || 0), 0) 
          : (formData.capacity ? parseInt(formData.capacity) : null),
        tickets: hasTickets ? tickets.map(t => ({
          name: t.name,
          price: t.price,
          quantity: t.quantity,
          sold: t.sold || 0,  // Preserve sold count in edit mode
          description: t.description || null
        })) : null,
        show_ticket_stats: hasTickets ? showTicketStats : true
      };

      // Use PUT for edit mode, POST for create mode
      if (isEditMode) {
        // Use the actual UUID, not the slug from URL
        const eventUuid = actualEventId || eventIdOrSlug;
        await axios.put(`${API}/events/${eventUuid}`, eventData);
        toast.success('Event updated successfully!');
        navigate(`/events/${eventIdOrSlug}`);
      } else {
        await axios.post(`${API}/events`, eventData);
        toast.success('Event submitted for approval!');
        navigate('/events');
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} event:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} event`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {initialLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Calendar className="w-7 h-7 text-primary" />
            {isEditMode ? 'Edit Event' : 'Create Event'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditMode ? 'Update your event details' : 'Share your event with the community'}
          </p>
        </div>

        {/* Info Notice - Only show for new events */}
        {!isEditMode && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 flex gap-3">
          <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-600 dark:text-amber-400">
            <strong>Note:</strong> Your event will be reviewed by our team before it appears publicly. This usually takes less than 24 hours.
          </div>
        </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 space-y-4">
            <h2 className="font-semibold text-foreground">Basic Information</h2>
            
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Beach Cleanup Day"
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category *
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      formData.category === cat.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{cat.icon}</span>
                    <span className="text-xs text-foreground">{cat.name.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Tell people what your event is about..."
                rows={4}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                required
              />
            </div>

            {/* Images Upload - Multi-image support */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Images ({images.length}/{MAX_IMAGES})
              </label>
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                Recommended size: <strong>1200 x 630 pixels</strong> (16:9 ratio). First image will be the poster.
              </p>
              
              {/* Uploaded Images Grid */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {images.map((img, index) => (
                    <div key={index} className="relative group aspect-video rounded-xl overflow-hidden border border-border">
                      <img src={img} alt={`Event ${index + 1}`} className="w-full h-full object-cover" />
                      {index === 0 && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                          Poster
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload Zone */}
              {images.length < MAX_IMAGES && (
                <label className="flex flex-col items-center justify-center w-full h-32 bg-secondary border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 transition-colors">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  {imageUploading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload images</span>
                      <span className="text-xs text-muted-foreground/70 mt-1">
                        {images.length === 0 ? 'Up to 10 images' : `${MAX_IMAGES - images.length} more allowed`}
                      </span>
                    </>
                  )}
                </label>
              )}
            </div>

            {/* Videos Upload */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Videos ({videos.length}/{MAX_VIDEOS})
              </label>
              <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
                <Video className="w-3.5 h-3.5" />
                Optional: Add up to 2 videos (max 100MB each)
              </p>
              
              {/* Uploaded Videos */}
              {videos.length > 0 && (
                <div className="space-y-3 mb-4">
                  {videos.map((vid, index) => (
                    <div key={index} className="relative flex items-center gap-3 p-3 bg-secondary rounded-xl border border-border">
                      <div className="w-24 h-16 bg-black rounded-lg overflow-hidden flex-shrink-0">
                        <video src={vid} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">Video {index + 1}</p>
                        <p className="text-xs text-muted-foreground truncate">{vid.split('/').pop()}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVideo(index)}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Video Upload Zone */}
              {videos.length < MAX_VIDEOS && (
                <label className="flex flex-col items-center justify-center w-full h-24 bg-secondary border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 transition-colors">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  {videoUploading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  ) : (
                    <>
                      <Video className="w-5 h-5 text-muted-foreground mb-1" />
                      <span className="text-sm text-muted-foreground">Click to upload video</span>
                      <span className="text-xs text-muted-foreground/70">{MAX_VIDEOS - videos.length} video(s) allowed</span>
                    </>
                  )}
                </label>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 space-y-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Date & Time
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  name="event_time"
                  value={formData.event_time}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  End Date (optional)
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  min={formData.event_date || new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  End Time (optional)
                </label>
                <input
                  type="time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 space-y-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Location
            </h2>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Island *
              </label>
              <MobileSelect
                name="island"
                value={formData.island}
                onChange={handleChange}
                options={[
                  { value: 'curacao', label: 'Curaçao' },
                  { value: 'aruba', label: 'Aruba' },
                  { value: 'bonaire', label: 'Bonaire' }
                ]}
                placeholder="Select island"
                label="Island"
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Venue Name (optional)
              </label>
              <input
                type="text"
                name="venue_name"
                value={formData.venue_name}
                onChange={handleChange}
                placeholder="e.g., Mambo Beach"
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Street address"
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City (optional)
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Willemstad"
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Pricing & Contact */}
          <div className="bg-card rounded-xl border border-border p-4 md:p-6 space-y-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" />
              Tickets & Pricing
            </h2>

            {/* Toggle for ticket types */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasTickets}
                  onChange={(e) => setHasTickets(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
              <span className="text-sm text-foreground">Enable multiple ticket types (VIP, Basic, etc.)</span>
            </div>

            {!hasTickets ? (
              /* Simple pricing */
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ticket Price (0 = Free)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0"
                      className="w-full pl-8 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Capacity (optional)
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    min="1"
                    placeholder="Max attendees"
                    className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            ) : (
              /* Multiple ticket types */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-foreground">
                    Ticket Types
                  </label>
                  <button
                    type="button"
                    onClick={addTicketType}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                  >
                    <Plus className="w-4 h-4" />
                    Add Ticket Type
                  </button>
                </div>

                {tickets.map((ticket, index) => (
                  <div key={index} className="p-4 bg-secondary/50 rounded-xl border border-border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">Ticket #{index + 1}</span>
                      {tickets.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTicketType(index)}
                          className="p-1 text-red-500 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid sm:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={ticket.name}
                        onChange={(e) => updateTicket(index, 'name', e.target.value)}
                        placeholder="e.g., VIP, Basic, Early Bird"
                        className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <input
                          type="number"
                          value={ticket.price}
                          onChange={(e) => updateTicket(index, 'price', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          placeholder="Price"
                          className="w-full pl-7 pr-3 py-2 bg-background border border-border rounded-lg text-sm"
                        />
                      </div>
                      <input
                        type="number"
                        value={ticket.quantity}
                        onChange={(e) => updateTicket(index, 'quantity', parseInt(e.target.value) || 0)}
                        min="1"
                        placeholder="Quantity"
                        className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                    
                    <input
                      type="text"
                      value={ticket.description}
                      onChange={(e) => updateTicket(index, 'description', e.target.value)}
                      placeholder="Description (optional) - e.g., Includes backstage access"
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                    />
                  </div>
                ))}

                <div className="p-3 bg-primary/10 rounded-lg">
                  <p className="text-sm text-foreground">
                    <strong>Total capacity:</strong> {tickets.reduce((sum, t) => sum + (t.quantity || 0), 0)} tickets
                  </p>
                </div>

                {/* Show Ticket Stats Toggle */}
                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg border border-border">
                  <div>
                    <p className="text-sm font-medium text-foreground">Show ticket availability</p>
                    <p className="text-xs text-muted-foreground">Display sold/available counts to visitors</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTicketStats}
                      onChange={(e) => setShowTicketStats(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                WhatsApp Number (optional)
              </label>
              <div className="relative">
                <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  name="organizer_whatsapp"
                  value={formData.organizer_whatsapp}
                  onChange={handleChange}
                  placeholder="+5999 123 4567"
                  className="w-full pl-12 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Allow people to contact you via WhatsApp about this event
              </p>
            </div>

            {/* Free Event Toggle */}
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-green-400">
                    Free Event
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Mark this as a free event (hides ticket button)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_free}
                    onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>

            {/* Ticket Link - only show if not free */}
            {!formData.is_free && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Ticket Link (optional)
                </label>
                <div className="relative">
                  <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="url"
                    name="external_link"
                    value={formData.external_link}
                    onChange={handleChange}
                    placeholder="https://tickets.example.com"
                    className="w-full pl-12 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Link where visitors can buy tickets
                </p>
              </div>
            )}

            {/* More Info Link */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                More Info Link (optional)
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="url"
                  name="info_url"
                  value={formData.info_url}
                  onChange={handleChange}
                  placeholder="https://example.com/event-info"
                  className="w-full pl-12 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Link to more information about the event
              </p>
            </div>

            {/* Social Links */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Social Links (optional)
              </label>
              
              {/* Facebook */}
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <input
                  type="url"
                  name="facebook_url"
                  value={formData.facebook_url}
                  onChange={handleChange}
                  placeholder="https://facebook.com/events/..."
                  className="w-full pl-12 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              
              {/* Instagram */}
              <div className="relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                <input
                  type="url"
                  name="instagram_url"
                  value={formData.instagram_url}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                  className="w-full pl-12 pr-4 py-3 bg-secondary border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isEditMode ? 'Saving...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Calendar className="w-5 h-5" />
                {isEditMode ? 'Save Changes' : 'Submit Event for Approval'}
              </>
            )}
          </button>
        </form>
      </div>
      )}
      
      <Footer />
    </div>
  );
};

export default CreateEventPage;
